import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../auth";
import { useNavigate } from "react-router-dom";
import OxyHeader from "../components/OxyHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Handshake, Copy, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Affiliate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [aff, setAff] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wd, setWd] = useState({ amount: 0, method: "bkash", account: "" });

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    api.get("/affiliate/me").then(r => setAff(r.data)).catch(() => {});
    api.get("/affiliate/withdrawals").then(r => setWithdrawals(r.data)).catch(() => {});
  }, [user]);

  const join = async () => {
    const r = await api.post("/affiliate/join");
    setAff(r.data); toast.success("Welcome to affiliate!");
  };

  const submitWithdraw = async () => {
    try {
      await api.post("/affiliate/withdraw", { amount: parseFloat(wd.amount), method: wd.method, account: wd.account });
      toast.success("Withdrawal requested");
      setWd({ amount: 0, method: "bkash", account: "" });
      api.get("/affiliate/withdrawals").then(r => setWithdrawals(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <OxyHeader/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center"><Handshake className="w-6 h-6"/></div>
          <div>
            <h1 className="text-2xl font-extrabold">Affiliate Program</h1>
            <p className="text-slate-500 text-sm font-bn">রেফার করুন, কমিশন উপার্জন করুন</p>
          </div>
        </div>

        {!aff ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
            <Handshake className="w-14 h-14 mx-auto text-emerald-500"/>
            <h3 className="mt-4 text-xl font-bold">Join the Affiliate Program</h3>
            <p className="text-slate-500 mt-2 font-bn">প্রতি পেইড কাস্টমারে ২০% কমিশন</p>
            <Button onClick={join} className="mt-6 bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid="join-affiliate">Join Now</Button>
          </div>
        ) : (
          <>
            <Card className="p-5 mb-5">
              <div className="text-xs font-bold uppercase text-slate-500">Your referral link</div>
              <div className="mt-2 flex items-center gap-2">
                <Input readOnly value={`${window.location.origin}/ref/${aff.code}`} data-testid="ref-link"/>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/ref/${aff.code}`); toast.success("Copied"); }}><Copy className="w-4 h-4"/></Button>
              </div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                ["Clicks", aff.clicks, "text-blue-600 bg-blue-50"],
                ["Signups", aff.signups, "text-purple-600 bg-purple-50"],
                ["Pending ৳", aff.pending_commission, "text-amber-600 bg-amber-50"],
                ["Approved ৳", aff.approved_commission, "text-emerald-600 bg-emerald-50"],
              ].map(([l, v, c], i) => (
                <div key={i} className="p-4 bg-white rounded-xl border">
                  <div className={`w-9 h-9 rounded-lg ${c} flex items-center justify-center mb-2`}><TrendingUp className="w-4 h-4"/></div>
                  <div className="text-xs text-slate-500 font-semibold uppercase">{l}</div>
                  <div className="text-2xl font-black font-mono">{v}</div>
                </div>
              ))}
            </div>

            <Card className="p-5 mb-6">
              <h3 className="font-bold mb-3 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-500"/>Request Withdrawal</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Amount ৳</Label><Input type="number" value={wd.amount} onChange={e => setWd({...wd, amount: e.target.value})} data-testid="wd-amount"/></div>
                <div>
                  <Label>Method</Label>
                  <select value={wd.method} onChange={e => setWd({...wd, method: e.target.value})} className="w-full h-10 rounded-md border border-slate-300 px-2">
                    <option value="bkash">bKash</option>
                    <option value="nagad">Nagad</option>
                    <option value="bank">Bank</option>
                  </select>
                </div>
                <div><Label>Account</Label><Input value={wd.account} onChange={e => setWd({...wd, account: e.target.value})} placeholder="01xxxxxxxxx"/></div>
              </div>
              <Button onClick={submitWithdraw} className="mt-3 bg-emerald-500 hover:bg-emerald-600" data-testid="wd-submit">Request</Button>
            </Card>

            <Card className="p-5">
              <h3 className="font-bold mb-3">Withdrawal History</h3>
              {withdrawals.length === 0 ? <div className="text-slate-400 text-sm">No withdrawals yet</div> : (
                <div className="space-y-2">
                  {withdrawals.map(w => (
                    <div key={w.withdrawal_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div>
                        <div className="font-mono font-bold">৳{w.amount}</div>
                        <div className="text-xs text-slate-500">{w.method} · {w.account}</div>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${w.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{w.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
